import { apiFetch } from '@/lib/api';
/**
 * The Demo Script — ordered story of the iMasterly experience.
 *
 * Each step:
 *   - id:        stable kebab-case identifier (URL-safe, sticks in sessionStorage)
 *   - act:       which "act" of the story this belongs to (for chapter UI)
 *   - role:      which seeded user should be signed in for this step.
 *                'none' means signed-out (e.g. landing pages, teacher join).
 *   - href:      where the demo bar takes the viewer when entering this step
 *   - narrator:  one-line "what's happening" shown in the demo bar
 *   - sub:       optional second line of context
 *   - highlight: optional CSS selector to put a coral glow on
 *
 * The script is intentionally LINEAR — viewers click Next, see what happens,
 * click Next. Branching would make the experience hard to follow.
 */

export type DemoRole =
  | 'none'
  | 'admin'
  | 'principal'
  | 'teacher_active'
  | 'parent_fresh';

export type DemoStep = {
  id: string;
  act: string;
  role: DemoRole;
  href: string;
  narrator: string;
  sub?: string;
  highlight?: string;
  // Optional async hook fired when the viewer clicks Next from THIS step.
  // Used for steps that need to commit a real action — e.g. submit a
  // prefilled form — before the URL advances. Awaited before navigation.
  // Skipped on Back navigation.
  beforeAdvance?: () => Promise<void> | void;
};

/**
 * Maps demo roles → seeded email. Single source of truth for who-becomes-who.
 * Used by DemoContext when advancing to a step.
 */
export const DEMO_ROLE_EMAIL: Record<DemoRole, string | null> = {
  none:             null,
  admin:            'demo+admin@imasterly.com',
  principal:        'demo+principal@imasterly.com',
  teacher_active:   'demo+teacher-anjali@imasterly.com',
  parent_fresh:     'demo+parent-meera@imasterly.com',
};

/**
 * Human-readable role labels for the demo bar's "You are now: …" chip.
 */
export const DEMO_ROLE_LABEL: Record<DemoRole, string> = {
  none:             'Signed out',
  admin:            'Super Admin',
  principal:        'Principal · Mrs. Sharma',
  teacher_active:   'Teacher · Ms. Anjali Patel',
  parent_fresh:     'Parent (new) · Mrs. Meera',
};

/**
 * The full script. Order matters — Next moves down this list, Back moves up.
 * Acts are pure UI grouping for the index page; they don't affect navigation.
 */
export const DEMO_STEPS: DemoStep[] = [
  // ── ACT 1 — A school appears on the map ────────────────────────────
  {
    id: 'intro',
    act: 'Welcome',
    role: 'none',
    href: '/demo/tour',
    narrator: 'A walk through how a school joins iMasterly, end to end.',
    sub: '6 roles, ~12 screens, real product — pre-filled so you don\'t have to type.',
  },
  {
    id: 'admin-schools-list',
    act: '1. A school comes on board',
    role: 'admin',
    href: '/admin/schools',
    narrator: 'A super-admin sees every onboarded school here.',
    sub: 'This is the admin\'s home base — every school they\'ve set up. Next: walk through how a school comes on board.',
  },
  {
    id: 'admin-fills-form',
    act: '1. A school comes on board',
    role: 'admin',
    href: '/admin/schools/new?demo=create-sunrise',
    narrator: 'Admin opens the new-school form, fields prefilled for Sunrise Demo School.',
    sub: 'UDISE, address, principal — every field a real onboarding asks for. Read at your pace, then click Next.',
    // When the viewer clicks Next, submit the form before advancing. The
    // form's own success handler redirects to /admin/schools — we still
    // navigate to the next step's href below to be deterministic.
    beforeAdvance: async () => {
      const w = window as any;
      if (typeof w.__demoSubmitNewSchoolForm === 'function') {
        w.__demoSubmitNewSchoolForm();
        // Give the POST a moment to complete + redirect to fire so the
        // next step doesn't race with the form's own router.replace.
        await new Promise(r => setTimeout(r, 900));
      }
    },
  },
  {
    id: 'admin-school-created',
    act: '1. A school comes on board',
    // Stay on /admin/schools/new — the form submission set `result`, so the
    // page now renders the success view with Mrs. Sharma's credentials, the
    // parent join URL, and the "welcome email sent" confirmation. THIS is
    // what the founder sees right after creating a school in real life.
    role: 'admin',
    href: '/admin/schools/new?demo=create-sunrise',
    narrator: 'Sunrise is onboarded. Mrs. Sharma\'s credentials show right here.',
    sub: 'Login URL, email, temp password — system also emails them to her. She\'ll be asked to change the password on first login.',
  },
  {
    id: 'principal-dashboard',
    act: '1. A school comes on board',
    // ONE step on /school/admin. Used to be two ("arrival" + "shares-link")
    // — same page, two narrators, redundant. Fold both beats together.
    role: 'principal',
    href: '/school/admin',
    narrator: 'Mrs. Sharma signs in. Day 1 — empty dashboard, one link to share with every family.',
    sub: 'WhatsApp preloaded message, copy link, or download QR. Reaches ~95% of families in under an hour.',
    highlight: 'input[readonly]',
  },

  // ── ACT 2 — A teacher joins ────────────────────────────────────────
  {
    id: 'teacher-discovers-school',
    act: '2. A teacher joins',
    role: 'none',
    href: '/s/sunrise',
    narrator: 'A teacher discovers the school\'s iMasterly page.',
    sub: 'The same /s/sunrise URL handles parents, teachers, and the principal.',
  },
  {
    id: 'teacher-requests-access',
    act: '2. A teacher joins',
    role: 'none',
    href: '/s/sunrise/teacher?demo=anjali-signup',
    narrator: 'Ms. Anjali fills the teacher signup — three fields, prefilled here for you.',
    sub: 'Name, email, password. On Next we submit for real → request lands on the principal\'s dashboard.',
    beforeAdvance: async () => {
      const w = window as any;
      if (typeof w.__demoSubmitTeacherSignup === 'function') {
        await w.__demoSubmitTeacherSignup();
      }
    },
  },
  {
    id: 'principal-sees-request',
    act: '2. A teacher joins',
    role: 'principal',
    href: '/school/admin',
    narrator: 'Mrs. Sharma sees the new request on her dashboard.',
    sub: 'Ms. Anjali Patel · PENDING. The principal reviews and approves.',
    highlight: 'main',
    // Auto-approve Anjali's request as we leave this step, so the next step's
    // teacher-dashboard view is the actual approved state, not pending.
    beforeAdvance: async () => {
      try {
        const reqs = await apiFetch('/api/school/auth/teacher-requests', { credentials: 'include' }).then(r => r.ok ? r.json() : null);
        const anjali = reqs?.teachers?.find((t: any) => /anjali/i.test(t.full_name || ''));
        if (anjali?.id) {
          await apiFetch(`/api/school/auth/teacher-requests/${anjali.id}/approve`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ gradeAssigned: null }),
          });
        }
      } catch (e) {
        console.warn('[demo] auto-approve Anjali failed (non-blocking):', e);
      }
    },
  },
  {
    id: 'teacher-anjali-active',
    act: '2. A teacher joins',
    role: 'teacher_active',
    href: '/teacher/dashboard',
    narrator: 'Once approved, Ms. Anjali signs in to her teacher dashboard.',
    sub: 'Roster, students, weak spots — all in one screen. Zero students for now.',
  },

  // ── ACT 3 — A family joins ─────────────────────────────────────────
  {
    id: 'parent-discovers-school',
    act: '3. A family joins',
    role: 'none',
    href: '/s/sunrise',
    narrator: 'A parent opens the same school link from WhatsApp.',
    sub: 'The tiles personalize once they sign in.',
  },
  {
    id: 'parent-signs-up',
    act: '3. A family joins',
    role: 'parent_fresh',
    href: '/s/sunrise/join',
    narrator: 'Mrs. Meera signs up at /s/sunrise/join — phone OTP or email.',
    sub: 'School context is parked server-side so her FIRST child auto-enrolls at Sunrise.',
  },
  {
    id: 'parent-dashboard',
    act: '3. A family joins',
    // Stay as Meera (parent_fresh) — same person who just signed up. The
    // ?demo=add-meera-child flag tells the StudentFormModal to prefill
    // Aarav Desai's details. The dashboard auto-opens the modal because
    // Meera has zero kids (reset-meera runs at step entry).
    role: 'parent_fresh',
    href: '/students?demo=add-meera-child',
    narrator: 'Mrs. Meera\'s dashboard auto-opens Add Student, prefilled for Aarav.',
    sub: 'The real product modal — Info / Subjects / Curriculum / Plan tabs.',
    // On Next, submit Aarav for real (POST /api/mathtutor/students). The next
    // step lands back on /students with NO ?demo= flag, so the modal stays
    // closed and Aarav shows up on the dashboard.
    beforeAdvance: async () => {
      try {
        await apiFetch('/api/mathtutor/students', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            firstName: 'Aarav',
            lastName: 'Desai',
            dateOfBirth: '2017-03-15',
            country: 'IN',
            state: '',
            board: 'CBSE',
            startingGrade: 4,
            gender: 'male',
            parentalConsent: true,
            parentalConsentDate: new Date().toISOString(),
          }),
        });
      } catch (e) {
        console.warn('[demo] aarav-create POST failed (non-blocking):', e);
      }
    },
  },
  {
    id: 'parent-sees-aarav',
    act: '3. A family joins',
    role: 'parent_fresh',
    href: '/students',
    narrator: 'Aarav is enrolled. Mrs. Meera can hand him the tablet now.',
    sub: 'His tile shows on her dashboard — one tap to start learning.',
    // On Next, mimic "Mrs. Meera taps Start Learning" — pick Aarav,
    // store his id in localStorage (what the parent dashboard does on
    // student-select), then advance to /dashboard for the kid view.
    beforeAdvance: async () => {
      try {
        const res = await apiFetch('/api/mathtutor/students', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const aarav = (data.students || []).find((s: any) => /aarav/i.test(s.name || ''));
        if (aarav && typeof window !== 'undefined') {
          window.localStorage.setItem('selectedStudentId', String(aarav.id));
        }
      } catch (e) {
        console.warn('[demo] select-aarav failed (non-blocking):', e);
      }
    },
  },
  {
    id: 'aarav-learning-home',
    act: '3. A family joins',
    role: 'parent_fresh',
    href: '/dashboard',
    narrator: "Aarav's learning home — every subject in one tap.",
    sub: 'Math, English, Science with personalized curricula. Plus practice modes, voice tutor, games.',
  },

  // ── ACT 4 — The closed loop ────────────────────────────────────────
  {
    id: 'teacher-sees-aarav',
    act: '4. Everyone stays in the loop',
    role: 'teacher_active',
    href: '/teacher/dashboard',
    narrator: 'Ms. Anjali refreshes and sees Aarav in her roster.',
    sub: 'Every child a teacher\'s class section enrolls shows up automatically.',
  },
  {
    id: 'principal-overview',
    act: '4. Everyone stays in the loop',
    role: 'principal',
    href: '/school/admin',
    narrator: 'The principal sees the live count climb.',
    sub: 'One active family today. By month two we expect ~80% adoption.',
  },
  {
    id: 'outro',
    act: 'Done',
    role: 'none',
    href: '/demo/tour',
    narrator: 'That\'s the full loop — from blank school to learning child.',
    sub: 'Real product, real APIs. Restart from the top, or exit demo mode.',
  },
];

/**
 * Group steps by act for the chapter list on /demo. Preserves order.
 */
export function groupedActs(): { act: string; steps: DemoStep[] }[] {
  const out: { act: string; steps: DemoStep[] }[] = [];
  for (const step of DEMO_STEPS) {
    const last = out[out.length - 1];
    if (last && last.act === step.act) last.steps.push(step);
    else out.push({ act: step.act, steps: [step] });
  }
  return out;
}
